import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  IRolesRepository,
  Role,
  RoleColumnPolicyEntry,
  RoleColumnVisibilityPatch,
  RoleUiLayoutEntry,
  CreateRoleInput,
  UpdateRoleInput,
  Permission,
  PermissionAction,
  VisibilityModule,
  SupervisorCapability,
} from '@erp/data-access/interfaces';

interface RoleRow {
  id: string;
  tenant_id: string;
  slug: string;
  name: string;
  description: string | null;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  landing_route?: string | null;
  kiosk?: boolean;
  kiosk_allowed_paths?: string[] | null;
  max_discount_percent?: number | null;
}

interface PermissionRow {
  role_id: string;
  module: string;
  action: PermissionAction;
}

interface MemberRoleRow {
  role_id: string;
}

/** Wraps PostgrestError into a real Error so consumer code's
 *  `e instanceof Error` checks match. Same helper as in
 *  invitations-repository.ts — kept private here to avoid a shared file. */
function rpcError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object') {
    const e = err as { message?: string; code?: string; details?: string; hint?: string };
    const wrapped = new Error(e.message ?? 'rpc_failed');
    if (e.code) (wrapped as Error & { code?: string }).code = e.code;
    if (e.details) (wrapped as Error & { details?: string }).details = e.details;
    if (e.hint) (wrapped as Error & { hint?: string }).hint = e.hint;
    return wrapped;
  }
  return new Error(String(err));
}

/** Roles + role_permissions live on master alongside tenants/tenant_members.
 *  Mutations route through SECURITY DEFINER RPCs (create/update/delete_custom_role).
 *  Reads are RLS-scoped to current_user_tenant_id(). */
export class SupabaseRolesRepository implements IRolesRepository {
  constructor(private supabase: SupabaseClient) {}

  async list(): Promise<Role[]> {
    // Roles for this tenant — RLS filters automatically.
    const { data: roles, error } = await this.supabase
      .from('roles')
      .select('*')
      .order('is_system', { ascending: false })
      .order('slug', { ascending: true });
    if (error) throw rpcError(error);
    const roleRows = (roles ?? []) as RoleRow[];
    if (roleRows.length === 0) return [];

    const ids = roleRows.map((r) => r.id);

    // One round trip for permissions, one for member rows. Two parallel
    // requests keep latency low. Member count is computed in JS — tenant
    // memberships are tiny (typically < 20 per tenant).
    const [permsRes, membersRes] = await Promise.all([
      this.supabase.from('role_permissions').select('*').in('role_id', ids),
      this.supabase.from('tenant_members').select('role_id').in('role_id', ids),
    ]);
    if (permsRes.error) throw rpcError(permsRes.error);
    if (membersRes.error) throw rpcError(membersRes.error);

    const byRole = new Map<string, Permission[]>();
    for (const p of (permsRes.data ?? []) as PermissionRow[]) {
      const list = byRole.get(p.role_id);
      if (list) list.push({ module: p.module, action: p.action });
      else byRole.set(p.role_id, [{ module: p.module, action: p.action }]);
    }

    const countByRole = new Map<string, number>();
    for (const m of (membersRes.data ?? []) as MemberRoleRow[]) {
      countByRole.set(m.role_id, (countByRole.get(m.role_id) ?? 0) + 1);
    }

    return roleRows.map((r) =>
      mapRole(r, byRole.get(r.id) ?? [], countByRole.get(r.id) ?? 0),
    );
  }

  async getById(id: string): Promise<Role | null> {
    const { data, error } = await this.supabase
      .from('roles')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw rpcError(error);
    if (!data) return null;
    const { data: perms, error: perr } = await this.supabase
      .from('role_permissions')
      .select('*')
      .eq('role_id', id);
    if (perr) throw rpcError(perr);
    const list = ((perms ?? []) as PermissionRow[]).map((p) => ({
      module: p.module,
      action: p.action,
    }));
    return mapRole(data as RoleRow, list);
  }

  async create(input: CreateRoleInput): Promise<Role> {
    const { data, error } = await this.supabase.rpc('create_custom_role', {
      p_name: input.name,
      p_description: input.description ?? null,
      p_permissions: input.permissions ?? [],
    });
    if (error) throw rpcError(error);
    const created = data as RoleRow;
    const fetched = await this.getById(created.id);
    if (!fetched) throw new Error('role_not_found_after_create');
    return fetched;
  }

  async update(id: string, input: UpdateRoleInput): Promise<Role> {
    const { data, error } = await this.supabase.rpc('update_custom_role', {
      p_role_id: id,
      p_name: input.name ?? null,
      p_description: input.description ?? null,
      p_permissions: input.permissions ?? null,
    });
    if (error) throw rpcError(error);
    const updated = data as RoleRow;
    const fetched = await this.getById(updated.id);
    if (!fetched) throw new Error('role_not_found_after_update');
    return fetched;
  }

  async setVisibility(id: string, modules: VisibilityModule[]): Promise<Role> {
    // Narrow sibling of update_custom_role: replaces only the
    // profit/costs/revenue grants, so it can safely run against the system
    // 'member' role (which update_custom_role refuses wholesale).
    const { error } = await this.supabase.rpc('set_role_visibility_permissions', {
      p_role_id: id,
      p_modules: modules,
    });
    if (error) throw rpcError(error);
    const fetched = await this.getById(id);
    if (!fetched) throw new Error('role_not_found_after_update');
    return fetched;
  }

  async setModulePermissions(
    id: string,
    parent: 'sales' | 'purchases',
    permissions: Permission[],
  ): Promise<Role> {
    // Sibling of set_role_visibility_permissions for the document families:
    // wholesale replace of `parent` + `parent.%`, system roles accepted,
    // owner/admin refused (migration 20260823210000).
    const { error } = await this.supabase.rpc('set_role_module_permissions', {
      p_role_id: id,
      p_parent: parent,
      p_permissions: permissions,
    });
    if (error) throw rpcError(error);
    const fetched = await this.getById(id);
    if (!fetched) throw new Error('role_not_found_after_update');
    return fetched;
  }

  async listColumnPolicies(): Promise<RoleColumnPolicyEntry[]> {
    // Plain tenant-scoped SELECT — the RLS policy on role_column_policies
    // already filters to the caller's tenant. Writes are impossible here:
    // the table only accepts the SECURITY DEFINER RPC below.
    const { data, error } = await this.supabase
      .from('role_column_policies')
      .select('role_id, table_key, policy');
    if (error) throw rpcError(error);
    return (data ?? []).map((row) => ({
      roleId: row.role_id as string,
      tableKey: row.table_key as string,
      policy: row.policy,
    }));
  }

  async patchColumnPolicyVisibility(
    roleId: string,
    tableKey: string,
    patch: RoleColumnVisibilityPatch,
    expected: RoleColumnVisibilityPatch,
  ): Promise<void> {
    const { error } = await this.supabase.rpc(
      'patch_role_column_policy_visibility',
      {
        p_role_id: roleId,
        p_table_key: tableKey,
        p_patch: patch,
        p_expected: expected,
      },
    );
    if (error) throw rpcError(error);
  }

  async listUiLayouts(): Promise<RoleUiLayoutEntry[]> {
    const { data, error } = await this.supabase
      .from('role_ui_layouts')
      .select('role_id, nav, dashboard');
    if (error) throw rpcError(error);
    return (data ?? []).map((row) => ({
      roleId: row.role_id as string,
      nav: row.nav,
      dashboard: row.dashboard,
    }));
  }

  async setUiLayout(
    roleId: string,
    nav: unknown | null,
    dashboard: unknown | null,
  ): Promise<void> {
    const { error } = await this.supabase.rpc('set_role_ui_layout', {
      p_role_id: roleId,
      p_nav: nav,
      p_dashboard: dashboard,
    });
    if (error) throw rpcError(error);
  }

  async setKioskConfig(
    id: string,
    config: {
      landingRoute: string | null;
      kiosk: boolean;
      allowedPaths: string[];
      pin?: string | null;
    },
  ): Promise<void> {
    const { error } = await this.supabase.rpc('set_role_kiosk_config', {
      p_role_id: id,
      p_landing: config.landingRoute,
      p_kiosk: config.kiosk,
      p_allowed_paths: config.allowedPaths,
      p_pin: config.pin ?? null,
    });
    if (error) throw rpcError(error);
  }

  async verifyExitPin(pin: string): Promise<SupervisorCapability | null> {
    const { data, error } = await this.supabase.rpc('verify_role_exit_pin', {
      p_pin: pin,
    });
    if (error) throw rpcError(error);
    return mapSupervisorCapability(data);
  }

  async validateSupervisor(token: string): Promise<SupervisorCapability | null> {
    const { data, error } = await this.supabase.rpc(
      'validate_kiosk_supervisor_session',
      { p_token: token },
    );
    if (error) throw rpcError(error);
    const capability = mapSupervisorCapability(data);
    return capability ? { ...capability, token } : null;
  }

  async revokeSupervisor(token: string): Promise<void> {
    const { error } = await this.supabase.rpc(
      'revoke_kiosk_supervisor_session',
      { p_token: token },
    );
    if (error) throw rpcError(error);
  }

  async setDiscountPolicy(id: string, maxDiscountPercent: number): Promise<void> {
    const { error } = await this.supabase.rpc('set_role_discount_policy', {
      p_role_id: id,
      p_max_discount_percent: maxDiscountPercent,
    });
    if (error) throw rpcError(error);
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.rpc('delete_custom_role', {
      p_role_id: id,
    });
    if (error) throw rpcError(error);
  }
}

function mapRole(
  r: RoleRow,
  permissions: Permission[],
  memberCount?: number,
): Role {
  return {
    id: r.id,
    tenantId: r.tenant_id,
    slug: r.slug,
    name: r.name,
    description: r.description,
    isSystem: r.is_system,
    permissions,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    ...(memberCount !== undefined ? { memberCount } : {}),
    // Kiosque (phase 4) — optionnels tant que la migration n'est pas partout.
    landingRoute: r.landing_route ?? null,
    kiosk: r.kiosk === true,
    kioskAllowedPaths: r.kiosk_allowed_paths ?? [],
    maxDiscountPercent:
      r.slug === 'owner' || r.slug === 'admin'
        ? 100
        : Number(r.max_discount_percent) || 0,
  };
}

function mapSupervisorCapability(value: unknown): SupervisorCapability | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as { valid?: unknown; token?: unknown; expiresAt?: unknown };
  if (row.valid !== true || typeof row.expiresAt !== 'string') return null;
  return {
    token: typeof row.token === 'string' ? row.token : '',
    expiresAt: row.expiresAt,
  };
}
